// MARCA | 06/05/2026 | New file – Cart upsell custom element with Swiper carousel (recently viewed products)
// MARCA | 03/06/2026 | Added morph + isMobileBreakpoint imports to support quick-buy modal for multi-variant products
// MARCA | 08/06/2026 | Added fallback collection support when fewer than minimum recently viewed products exist
// MARCA | 11/06/2026 | Added quick-add threshold product button handler and visibility logic for AOV boost card
import { Component } from "@theme/component";
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from "@theme/morph";
import { isMobileBreakpoint } from "@theme/utilities";

/** @type {string} Section ID used by the Section Rendering API for recently viewed product cards */
const RECENTLY_VIEWED_SECTION_ID = 'cart-upsell-recently-viewed';

/**
 * Custom element for the cart upsell carousel.
 * Initialises Swiper and handles add-to-cart for upsell products.
 * Sources products from recently viewed (localStorage + Section Rendering API).
 *
 * @extends {Component}
 */
class CartUpsell extends Component {
  /** @type {any} Swiper instance (loaded from CDN) */
  #swiper = null;

  connectedCallback() {
    super.connectedCallback();
    this.#loadRecentlyViewedProducts();
    // MARCA | 11/06/2026 | Bind quick-add button independently of carousel load
    this.#bindQuickAddButton();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroySwiper();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #destroySwiper() {
    if (this.#swiper) {
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    }
  }

  #initSwiper() {
    const SwiperConstructor = /** @type {any} */ (globalThis).Swiper;
    if (!SwiperConstructor) return;

    const el = this.querySelector(".cart-upsell__swiper");
    if (!el) return;

    this.#swiper = new SwiperConstructor(el, {
      slidesPerView: 2.3,
      spaceBetween: 12,
      freeMode: true,
      watchOverflow: true,
    });
  }

  #bindAddButtons() {
    this.querySelectorAll(".cart-upsell__add-button").forEach((button) => {
      button.addEventListener("click", (e) => this.#handleAdd(e));
    });
  }

  // MARCA | 11/06/2026 | Bind the quick-add threshold product button
  #bindQuickAddButton() {
    const btn = this.querySelector(".cart-upsell-quick-add__add-btn");
    if (btn) btn.addEventListener("click", (e) => this.#handleQuickAdd(e));
  }

  // MARCA | 11/06/2026 | Add quick-add threshold product to cart via cart/add.js
  /**
   * Handles the compact quick-add button click. Adds the single-variant
   * product directly to cart and dispatches a CartAddEvent so the cart
   * re-renders (which hides this card if the product is now in cart).
   *
   * @param {Event} event
   */
  async #handleQuickAdd(event) {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;

    try {
      const shopify = /** @type {any} */ (window.Shopify);
      const routes = shopify?.routes?.root || "/";

      const response = await fetch(routes + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        }),
      });

      if (!response.ok) throw new Error("Failed to add quick-add product");
      await response.json();

      const cartResponse = await fetch(routes + "cart.js");
      const cartData = await cartResponse.json();

      // MARCA | 11/06/2026 | Hide card immediately – morph skips this subtree (data-skip-subtree-update)
      this.#hideQuickAdd();

      document.dispatchEvent(
        new CartAddEvent(cartData, "cart-upsell-quick-add", {
          itemCount: cartData.item_count,
          source: "cart-upsell-quick-add",
        }),
      );
    } catch (error) {
      console.error("Failed to add quick-add product:", error);
      button.disabled = false;
    }
  }

  // MARCA | 11/06/2026 | Remove the quick-add card from the DOM after a successful add
  #hideQuickAdd() {
    const card = this.querySelector(".cart-upsell-quick-add");
    if (card) card.remove();
    this.removeAttribute("data-has-quick-add");
  }

  /**
   * Fetch recently viewed product cards via the Section Rendering API.
   * Reads product entries from localStorage, builds a search URL, and injects the
   * server-rendered slides into the Swiper carousel.
   *
   * MARCA | 08/06/2026 | Falls back to static collection when recently viewed count is below threshold
   */
  async #loadRecentlyViewedProducts() {
    const viewedProducts = RecentlyViewed.getProducts();
    const minProducts = parseInt(this.dataset.minRecentlyViewed || "3", 10);

    if (viewedProducts.length < minProducts) {
      await this.#loadFallbackCollection();
      return;
    }

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set(
        "q",
        viewedProducts
          .map(
            (/** @type {{ id: string, handle: string }} */ entry) =>
              `id:${entry.id}`,
          )
          .join(" OR "),
      );
      url.searchParams.set("type", "product");

      const html = await sectionRenderer.getSectionHTML(
        RECENTLY_VIEWED_SECTION_ID,
        false,
        url,
      );
      if (!html) {
        await this.#loadFallbackCollection();
        return;
      }

      const doc = new DOMParser().parseFromString(html, "text/html");
      const slides = doc.querySelectorAll(".swiper-slide");

      if (slides.length < minProducts) {
        await this.#loadFallbackCollection();
        return;
      }

      const swiperWrapper = this.querySelector(".swiper-wrapper");
      if (!swiperWrapper) return;

      this.#destroySwiper();
      swiperWrapper.innerHTML = "";

      const slidesByProductId = new Map();
      for (const slide of slides) {
        const card = slide.querySelector("[data-product-id]");
        if (card instanceof HTMLElement) {
          slidesByProductId.set(card.dataset.productId, slide);
        }
      }

      for (const entry of viewedProducts) {
        const slide = slidesByProductId.get(entry.id);
        if (slide) swiperWrapper.appendChild(slide);
      }

      this.#setHeading(this.dataset.headingRecentlyViewed || "Recently Viewed");
      this.hidden = false;
      this.#initSwiper();
      this.#bindAddButtons();
    } catch (error) {
      console.error("Failed to load recently viewed products:", error);
      await this.#loadFallbackCollection();
    }
  }

  // MARCA | 08/06/2026 | Load fallback collection products when recently viewed is insufficient
  /**
   * Fetches product cards from the fallback collection via the Section Rendering API.
   * Shows the "You May Also Like" heading instead of "Recently Viewed".
   */
  async #loadFallbackCollection() {
    const fallbackCollectionHandle = this.dataset.fallbackCollection;
    if (!fallbackCollectionHandle) {
      this.#destroySwiper();
      // MARCA | 11/06/2026 | Keep element visible when quick-add card exists
      this.hidden = !this.#hasQuickAdd();
      return;
    }

    try {
      const url = new URL(
        `/collections/${fallbackCollectionHandle}`,
        location.origin,
      );

      const html = await sectionRenderer.getSectionHTML(
        RECENTLY_VIEWED_SECTION_ID,
        false,
        url,
      );
      if (!html) {
        this.#destroySwiper();
        // MARCA | 11/06/2026 | Keep element visible when quick-add card exists
        this.hidden = !this.#hasQuickAdd();
        return;
      }

      const doc = new DOMParser().parseFromString(html, "text/html");
      const slides = doc.querySelectorAll(".swiper-slide");

      if (slides.length === 0) {
        this.#destroySwiper();
        // MARCA | 11/06/2026 | Keep element visible when quick-add card exists
        this.hidden = !this.#hasQuickAdd();
        return;
      }

      const swiperWrapper = this.querySelector(".swiper-wrapper");
      if (!swiperWrapper) return;

      this.#destroySwiper();
      swiperWrapper.innerHTML = "";

      for (const slide of slides) {
        swiperWrapper.appendChild(slide);
      }

      this.#setHeading(this.dataset.headingFallback || "Suggested products");
      this.hidden = false;
      this.#initSwiper();
      this.#bindAddButtons();
    } catch (error) {
      console.error("Failed to load fallback collection:", error);
      // MARCA | 11/06/2026 | Keep element visible when quick-add card exists
      this.hidden = !this.#hasQuickAdd();
    }
  }

  // MARCA | 08/06/2026 | Update the accordion heading text dynamically
  /**
   * Updates the visible accordion heading text.
   * @param {string} text
   */
  #setHeading(text) {
    const headingEl = this.querySelector(".cart-upsell__heading");
    if (headingEl) headingEl.textContent = text;
  }

  // MARCA | 11/06/2026 | Check whether a server-rendered quick-add card is present in the DOM
  /** @returns {boolean} */
  #hasQuickAdd() {
    return this.hasAttribute("data-has-quick-add");
  }

  // MARCA | 03/06/2026 | Route multi-variant products to the quick-buy modal instead of direct add-to-cart
  /**
   * Determines whether a product has variants and routes to the appropriate action:
   * - Single variant → add directly to cart
   * - Multiple variants → open quick-add modal for size/option selection
   *
   * @param {Event} event
   */
  async #handleAdd(event) {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const card = button.closest(".cart-upsell-item");
    if (!card || !(card instanceof HTMLElement)) return;

    const variantsCount = parseInt(card.dataset.variantsCount || "1", 10);

    if (variantsCount > 1) {
      await this.#openQuickAddModal(card, button);
    } else {
      await this.#addDirectlyToCart(button);
    }
  }

  // MARCA | 03/06/2026 | Fetch product page and open the quick-buy modal for variant selection
  /**
   * Opens the quick-add modal for a multi-variant product by fetching the product page
   * and morphing its content into the global quick-add dialog. Mirrors the pattern used
   * by QuickAddComponent.handleClick on product cards.
   *
   * @param {HTMLElement} card - The upsell card element containing product data attributes.
   * @param {HTMLButtonElement} button - The clicked button (for loading state).
   */
  async #openQuickAddModal(card, button) {
    const productUrl = card.dataset.productUrl;
    if (!productUrl) return;

    const textEl = button.querySelector(".cart-upsell__button-text");
    const originalText = textEl?.textContent || "";
    button.disabled = true;
    if (textEl) textEl.textContent = "Loading...";

    try {
      const response = await fetch(productUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const productGrid = html.querySelector("[data-product-grid-content]");

      if (!productGrid) {
        await this.#addDirectlyToCart(button);
        return;
      }

      const modalContent = document.getElementById("quick-add-modal-content");
      if (!modalContent) return;

      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));

      if (isMobileBreakpoint()) {
        const productDetails = freshContent.querySelector(".product-details");
        const productFormComponent = freshContent.querySelector(
          "product-form-component",
        );
        const variantPicker = freshContent.querySelector("variant-picker");
        const productPrice = freshContent.querySelector("product-price");

        const productTitle = document.createElement("a");
        productTitle.textContent = card.dataset.productTitle || "";
        productTitle.href = productUrl;

        const productHeader = document.createElement("div");
        productHeader.classList.add("product-header");

        const vendor = card.dataset.productVendor;
        if (vendor) {
          const vendorEl = document.createElement("span");
          vendorEl.classList.add("product-header__vendor");
          vendorEl.textContent = vendor;
          productHeader.appendChild(vendorEl);
        }

        productHeader.appendChild(productTitle);
        if (productPrice) productHeader.appendChild(productPrice);
        freshContent.appendChild(productHeader);
        if (variantPicker) freshContent.appendChild(variantPicker);
        if (productFormComponent)
          freshContent.appendChild(productFormComponent);
        productDetails?.remove();
      }

      morph(modalContent, freshContent);

      const dialogComponent = document.getElementById("quick-add-dialog");
      if (dialogComponent && "showDialog" in dialogComponent) {
        /** @type {any} */ (dialogComponent).showDialog();
      }

      /** @type {any} */ (window).Klarna?.OnsiteMessaging?.refresh();
    } catch (error) {
      console.error("Failed to open quick-add modal:", error);
    } finally {
      button.disabled = false;
      if (textEl) textEl.textContent = originalText;
    }
  }

  // MARCA | 03/06/2026 | Extracted direct add-to-cart into its own method for single-variant products
  /**
   * Directly adds a single-variant product to cart without opening the modal.
   *
   * @param {HTMLButtonElement} button
   */
  async #addDirectlyToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    const textEl = button.querySelector(".cart-upsell__button-text");
    const originalText = textEl?.textContent || "";
    if (textEl) textEl.textContent = "Adding...";

    try {
      const shopify = /** @type {any} */ (window.Shopify);
      const routes = shopify?.routes?.root || "/";

      const response = await fetch(routes + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        }),
      });

      if (!response.ok) throw new Error("Failed to add upsell product");

      await response.json();

      const cartResponse = await fetch(routes + "cart.js");
      const cartData = await cartResponse.json();

      document.dispatchEvent(
        new CartAddEvent(cartData, "cart-upsell", {
          itemCount: cartData.item_count,
          source: "cart-upsell",
        }),
      );

      if (textEl) textEl.textContent = "Added!";
      setTimeout(() => {
        button.disabled = false;
        if (textEl) textEl.textContent = originalText;
      }, 1500);
    } catch (error) {
      console.error("Failed to add upsell product:", error);
      button.disabled = false;
      if (textEl) textEl.textContent = originalText;
    }
  }

  #onCartUpdate = () => {
    requestAnimationFrame(() => {
      this.#loadRecentlyViewedProducts();
    });
  };
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
